import { matchPath, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Bot,
  Bell,
  Boxes,
  ChevronDown,
  ClipboardList,
  FileText,
  Image,
  LayoutDashboard,
  type LucideIcon,
  Menu,
  MessageSquare,
  Moon,
  Package,
  Search,
  PanelsTopLeft,
  Receipt,
  RotateCcw,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Sun,
  Tags,
  Truck,
  Users,
  Wallet,
  Warehouse,
  LogOut,
  X,
} from 'lucide-react';

import { BrandMark, Logo } from '@/components/brand/logo';
import { useAuth } from '@/lib/auth-context';
import { useQueueSummary } from '@/lib/customers-api';
import { humanize } from '@/lib/status';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

interface NavLeaf {
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: number;
  /** Badge sourced from live data rather than a fixed number. */
  badgeKey?: 'awaitingReview';
}

interface NavParent {
  label: string;
  icon: LucideIcon;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavParent;

const isParent = (entry: NavEntry): entry is NavParent => 'children' in entry;

/**
 * Grouped by the job being done, not by data model: everything a sales desk
 * touches for one order sits under Sales, everything that moves stock under
 * Inventory.
 */
const NAV: NavEntry[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard },
  {
    label: 'Sales',
    icon: ShoppingCart,
    children: [
      { label: 'Orders', to: '/orders', icon: ShoppingCart, badge: 32 },
      { label: 'Quotations', to: '/quotations', icon: FileText },
      { label: 'Bulk Inquiries', to: '/inquiries', icon: MessageSquare, badge: 18 },
      { label: 'Payments', to: '/payments', icon: Receipt },
      { label: 'Refunds', to: '/refunds', icon: RotateCcw },
    ],
  },
  {
    label: 'Catalog',
    icon: Package,
    children: [
      { label: 'Products', to: '/products', icon: Package },
      { label: 'Categories', to: '/categories', icon: Tags },
      { label: 'Brands', to: '/brands', icon: PanelsTopLeft },
    ],
  },
  {
    label: 'Inventory',
    icon: Boxes,
    children: [
      { label: 'Stock', to: '/inventory', icon: Boxes },
      { label: 'Warehouses', to: '/warehouses', icon: Warehouse },
      { label: 'Shipping', to: '/shipping', icon: Truck },
    ],
  },
  {
    label: 'Customers',
    icon: Users,
    children: [
      { label: 'All Customers', to: '/customers', icon: Users },
      { label: 'Verification', to: '/customers/verification', icon: ShieldCheck, badgeKey: 'awaitingReview' },
      { label: 'Security Deposits', to: '/deposits', icon: Wallet },
    ],
  },
  { label: 'AI Assistant', to: '/ai-assistant', icon: Bot },
  {
    label: 'Content',
    icon: ClipboardList,
    children: [
      { label: 'Banners', to: '/banners', icon: Image },
      { label: 'Site Content', to: '/cms', icon: ClipboardList },
      // Under Content rather than Settings: SEO is copy somebody writes, and
      // the person writing it is already in this group.
      { label: 'SEO', to: '/seo', icon: Search },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    children: [
      { label: 'Users & Roles', to: '/users', icon: Users },
      { label: 'General Settings', to: '/settings', icon: Settings },
      { label: 'Audit Logs', to: '/audit-logs', icon: ShieldCheck },
    ],
  },
];

const ALL_PATHS = NAV.flatMap((entry) => (isParent(entry) ? entry.children : [entry])).map(
  (leaf) => leaf.to,
);

/**
 * Exact match for any path that prefixes another item's — otherwise
 * `/customers` lights up on `/customers/verification` and both look active.
 */
const needsExactMatch = (to: string): boolean =>
  to === '/' || ALL_PATHS.some((other) => other !== to && other.startsWith(`${to}/`));

const isLeafActive = (leaf: NavLeaf, pathname: string): boolean =>
  matchPath({ path: leaf.to, end: needsExactMatch(leaf.to) }, pathname) !== null;

const COLLAPSED_STORAGE_KEY = 'bulk3-admin-sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // A convenience only; the sidebar still toggles for this visit.
  }
}

function CountBadge({ value, inverted }: { value: number | undefined; inverted?: boolean }) {
  if (value === undefined || value <= 0) {
    return null;
  }

  return (
    <span
      className={cn(
        'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
        inverted ? 'bg-white/20 text-white' : 'bg-danger-50 text-danger-700',
      )}
    >
      {value}
    </span>
  );
}

function SidebarContent({
  collapsed = false,
  onNavigate,
  onExpand,
}: {
  /** Icon rail: labels and children hidden. Desktop only. */
  collapsed?: boolean;
  onNavigate?: () => void;
  /** Asked to widen the rail so a parent's children can be shown. */
  onExpand?: () => void;
}) {
  const summary = useQueueSummary();
  const { pathname } = useLocation();
  // Explicit open/closed choices. A parent with no entry here follows the
  // route: open while one of its children is the current page.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const badgeFor = (leaf: NavLeaf): number | undefined =>
    leaf.badgeKey === 'awaitingReview' ? summary.data?.awaitingReview : leaf.badge;

  const leafClass = (active: boolean, child: boolean) =>
    cn(
      'flex items-center gap-2.5 rounded-lg text-sm transition-colors',
      collapsed ? 'justify-center px-0 py-2' : 'px-2.5 py-2',
      child && !active && 'text-ink-muted hover:bg-canvas hover:text-ink',
      child && active && 'bg-brand-50 font-medium text-brand-700',
      !child && !active && 'text-ink-muted hover:bg-canvas hover:text-ink',
      !child && active && 'bg-brand-600 font-medium text-white',
    );

  const renderLeaf = (leaf: NavLeaf, child = false) => (
    <li key={leaf.to}>
      <NavLink
        to={leaf.to}
        end={needsExactMatch(leaf.to)}
        onClick={onNavigate}
        title={collapsed ? leaf.label : undefined}
        aria-label={collapsed ? leaf.label : undefined}
        className={({ isActive }) => leafClass(isActive, child)}
      >
        {({ isActive }) => (
          <>
            <leaf.icon className={cn('shrink-0', child ? 'size-3.5' : 'size-4')} aria-hidden="true" />
            {!collapsed && (
              <>
                <span className="flex-1 truncate">{leaf.label}</span>
                <CountBadge value={badgeFor(leaf)} inverted={isActive && !child} />
              </>
            )}
          </>
        )}
      </NavLink>
    </li>
  );

  const renderParent = (parent: NavParent) => {
    const hasActiveChild = parent.children.some((leaf) => isLeafActive(leaf, pathname));
    const open = !collapsed && (toggled[parent.label] ?? hasActiveChild);
    const groupId = `nav-group-${parent.label.toLowerCase().replace(/\W+/g, '-')}`;
    const total = parent.children.reduce((sum, leaf) => sum + (badgeFor(leaf) ?? 0), 0);

    return (
      <li key={parent.label}>
        <button
          type="button"
          onClick={() => {
            if (collapsed) {
              // The rail has no room for children: widen it, then show them.
              setToggled((current) => ({ ...current, [parent.label]: true }));
              onExpand?.();
              return;
            }
            setToggled((current) => ({ ...current, [parent.label]: !open }));
          }}
          aria-expanded={collapsed ? undefined : open}
          aria-controls={collapsed ? undefined : groupId}
          title={collapsed ? parent.label : undefined}
          aria-label={collapsed ? parent.label : undefined}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-lg text-sm transition-colors hover:bg-canvas',
            collapsed ? 'justify-center px-0 py-2' : 'px-2.5 py-2',
            hasActiveChild ? 'font-medium text-ink' : 'text-ink-muted hover:text-ink',
            collapsed && hasActiveChild && 'bg-brand-50 text-brand-700',
          )}
        >
          <parent.icon className="size-4 shrink-0" aria-hidden="true" />
          {!collapsed && (
            <>
              <span className="flex-1 truncate text-left">{parent.label}</span>
              {/* The children's counts would be hidden while closed; surface their sum. */}
              {!open && <CountBadge value={total} />}
              <ChevronDown
                className={cn('size-4 shrink-0 text-ink-subtle transition-transform', open && 'rotate-180')}
                aria-hidden="true"
              />
            </>
          )}
        </button>

        {open && (
          <ul id={groupId} className="mt-0.5 ml-[1.15rem] space-y-0.5 border-l border-line pl-2">
            {parent.children.map((leaf) => renderLeaf(leaf, true))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'flex h-14 shrink-0 items-center border-b border-line',
          collapsed ? 'justify-center px-2' : 'gap-2 px-4',
        )}
      >
        {collapsed ? <BrandMark /> : <Logo />}
      </div>

      <nav aria-label="Admin" className={cn('flex-1 overflow-y-auto', collapsed ? 'p-2' : 'p-3')}>
        <ul className="space-y-0.5">
          {NAV.map((entry) => (isParent(entry) ? renderParent(entry) : renderLeaf(entry)))}
        </ul>
      </nav>
    </div>
  );
}

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const setCollapsedAndRemember = (next: boolean) => {
    setCollapsed(next);
    writeCollapsed(next);
  };

  // One hamburger for both sizes: below `lg` it opens the drawer, above it
  // folds the sidebar to an icon rail.
  const onMenuClick = () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setCollapsedAndRemember(!collapsed);
    } else {
      setMobileOpen(true);
    }
  };

  useEffect(() => {
    if (!mobileOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '—';

  return (
    <div className="min-h-dvh">
      {/* Fixed sidebar on desktop; slide-over on mobile. */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden border-r border-line bg-surface transition-[width] duration-200 lg:block',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <SidebarContent collapsed={collapsed} onExpand={() => setCollapsedAndRemember(false)} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-overlay"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-64 bg-surface shadow-popover">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute right-2 top-2.5 grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-canvas"
              aria-label="Close menu"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className={cn('transition-[padding] duration-200', collapsed ? 'lg:pl-16' : 'lg:pl-60')}>
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-surface px-4">
          <button
            type="button"
            onClick={onMenuClick}
            className="grid size-9 shrink-0 place-items-center rounded-lg text-ink-muted hover:bg-canvas hover:text-ink"
            aria-label="Toggle menu"
            title="Menu"
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>

          <div className="relative hidden max-w-md flex-1 sm:block">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="Search orders, customers, products..."
              aria-label="Search"
              className="w-full rounded-lg border border-line bg-canvas py-2 pl-9 pr-3 text-sm placeholder:text-ink-subtle focus:border-brand-500 focus:bg-surface focus:outline-none"
            />
          </div>

          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={toggleTheme}
              className="grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-canvas hover:text-ink"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? (
                <Sun className="size-5" aria-hidden="true" />
              ) : (
                <Moon className="size-5" aria-hidden="true" />
              )}
            </button>

            <button
              type="button"
              className="relative grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-canvas"
              aria-label="Notifications"
            >
              <Bell className="size-5" aria-hidden="true" />
              <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-danger-600" />
            </button>

            <NavLink
              to="/profile"
              title="My profile"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-canvas',
                  isActive && 'bg-canvas',
                )
              }
            >
              <span className="grid size-8 place-items-center rounded-full bg-brand-600 text-xs font-semibold text-white">
                {initials}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-medium text-ink">
                  {user ? `${user.firstName} ${user.lastName}` : '—'}
                </span>
                <span className="block text-[11px] text-ink-subtle">
                  {user ? humanize(user.role) : ''}
                </span>
              </span>
            </NavLink>

            <button
              type="button"
              onClick={() => void signOut()}
              className="grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-canvas"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="size-5" aria-hidden="true" />
            </button>
          </div>
        </header>

        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
